import {useMemo, useState, type ReactNode} from "react";
import {User} from "../../../api/accessControl/User.ts";
import type {Money} from "../../../lib/Money.ts";
import {DrillDownDrawer, type DrillDownLine} from "../DrillDown/index.ts";
import type {LegalEntityType} from "../../../generated/enums.ts";
import {
    AccountWarningsNotice,
    BankBalanceChecks,
    PeriodicBalanceSheetTable,
    PeriodicToolbar,
} from "./components";
import type {
    PeriodicBalanceSheetReport,
    PeriodicBalanceSheetRow,
    PeriodicBalanceSheetViewRow,
} from "./types";
import {
    balanceColumnsForBuckets,
    hasBalanceSheetActivity,
    hasWarnedRows,
    linesUpTo,
    rowLabel,
} from "./utils";
import {
    usePeriodicStatement,
    type FetchPeriodicStatement,
    type PeriodicStatementCopy,
} from "./usePeriodicStatement.ts";

const BALANCE_SHEET_COPY: PeriodicStatementCopy = {
    loadFailedTitle: "Kon de balans per maand niet laden",
    refreshedTitle: "Balans per maand vernieuwd",
    refreshFailedDescription: "Kon de balans per maand niet vernieuwen. Probeer het opnieuw.",
    logName: "monthly balance sheet",
};

export interface PeriodicBalanceSheetViewProps {
    currentUser: User;
    businessId: string;
    legalEntityType: LegalEntityType;
    fetchBalanceSheetPerPeriod: FetchPeriodicStatement<PeriodicBalanceSheetReport>;
    refreshTrialBalanceCache: () => Promise<{invalidated_count: number}>;
    toolbarLeading?: ReactNode;
}

export default function PeriodicBalanceSheetView({
    currentUser,
    businessId,
    legalEntityType,
    fetchBalanceSheetPerPeriod,
    refreshTrialBalanceCache,
    toolbarLeading,
}: PeriodicBalanceSheetViewProps) {
    const statement = usePeriodicStatement<PeriodicBalanceSheetRow, PeriodicBalanceSheetReport>({
        userId: currentUser.id,
        businessId,
        legalEntityType,
        fetchReport: fetchBalanceSheetPerPeriod,
        refreshTrialBalanceCache,
        copy: BALANCE_SHEET_COPY,
    });
    const {reportData, rows, visibleRows, isLoading, searchQuery} = statement;

    const [isDrillDownOpen, setIsDrillDownOpen] = useState(false);
    const [selectedLines, setSelectedLines] = useState<DrillDownLine[]>([]);
    const [selectedOpeningBalance, setSelectedOpeningBalance] = useState<Money | undefined>();
    const [selectedRowTitle, setSelectedRowTitle] = useState("");

    const columns = useMemo(
        () => (reportData ? balanceColumnsForBuckets(reportData.buckets) : []),
        [reportData],
    );

    const hasActivity = hasBalanceSheetActivity(rows);

    function openDrawer(row: PeriodicBalanceSheetViewRow, periodIndex: number) {
        const bucket = reportData?.buckets[periodIndex];
        if (!bucket) return;
        const code = row.account?.code;
        const label = rowLabel(row, legalEntityType);
        setSelectedLines(linesUpTo(row.lines, bucket.end_date));
        setSelectedOpeningBalance(row.opening_balance);
        setSelectedRowTitle(`${code ? `${code} - ` : ""}${label} — ${columns[periodIndex].label}`);
        setIsDrillDownOpen(true);
    }

    const searchNoAccounts =
        !isLoading &&
        !!searchQuery.trim() &&
        hasActivity &&
        !visibleRows.some((row) => row.account);

    return (
        <>
            <PeriodicToolbar
                dateFrom={statement.dateFrom}
                dateTo={statement.dateTo}
                onDateRangeChange={statement.handleDateRangeChange}
                leading={toolbarLeading}
                selectedCurrency={statement.selectedCurrency}
                currencyOptions={statement.currencyOptions}
                onCurrencyChange={statement.setSelectedCurrency}
                showPredictedAccounts={statement.showPredictedAccounts}
                onPredictedChange={statement.handlePredictedChange}
                searchQuery={searchQuery}
                onSearchChange={statement.setSearchQuery}
                onRefresh={() => void statement.refresh()}
                isRefreshingCache={statement.isRefreshingCache}
                isLoading={isLoading}
                beyondDefault={statement.beyondDefault}
                onToggleAll={statement.handleToggleAll}
            />

            <BankBalanceChecks checks={reportData?.bank_balance_checks ?? null} />
            <AccountWarningsNotice visible={hasWarnedRows(rows)} />

            <div className="relative min-h-[12rem] w-full">
                <div className="my-4">
                    <PeriodicBalanceSheetTable
                        loading={isLoading}
                        skeletonMonthCount={statement.skeletonMonthCount}
                        rows={hasActivity && !searchNoAccounts ? visibleRows : []}
                        columns={columns}
                        currency={statement.selectedCurrency}
                        legalEntityType={legalEntityType}
                        onToggleNode={statement.handleToggleNode}
                        onCellClick={openDrawer}
                        emptyMessage={
                            searchNoAccounts
                                ? `Geen rekeningen gevonden voor “${searchQuery.trim()}”.`
                                : undefined
                        }
                    />
                </div>
            </div>

            <DrillDownDrawer
                open={isDrillDownOpen}
                onClose={() => setIsDrillDownOpen(false)}
                title={selectedRowTitle}
                businessId={businessId}
                rows={selectedLines}
                openingBalance={selectedOpeningBalance}
                headingPrefix="Bronnen"
                emptyMessage="Er zijn geen facturen of transacties gevonden voor deze rekening."
            />
        </>
    );
}
